import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { TURNSTILE_SITE_KEY, loadTurnstile, turnstileEnabled } from "@/lib/turnstile";

type TurnstileStatus = "disabled" | "loading" | "ready" | "error";

type TurnstileContextValue = {
  status: TurnstileStatus;
  /** Liefert einen gültigen Token (löst bei Bedarf eine neue Challenge aus). */
  getToken: () => Promise<string | null>;
  /** Verbrauchten Token verwerfen und neuen anfordern. */
  reset: () => void;
};

const TurnstileContext = createContext<TurnstileContextValue>({
  status: "disabled",
  getToken: async () => null,
  reset: () => {},
});

export const useTurnstile = () => useContext(TurnstileContext);

export function TurnstileProvider({ children }: { children: React.ReactNode }) {
  const enabled = turnstileEnabled();
  const [status, setStatus] = useState<TurnstileStatus>(enabled ? "loading" : "disabled");
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const tokenRef = useRef<string | null>(null);
  const waitersRef = useRef<((token: string | null) => void)[]>([]);

  const resolveWaiters = useCallback((token: string | null) => {
    const waiters = waitersRef.current;
    waitersRef.current = [];
    waiters.forEach((fn) => fn(token));
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    loadTurnstile()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          size: "invisible",
          appearance: "interaction-only",
          retry: "auto",
          callback: (token: string) => {
            tokenRef.current = token;
            setStatus("ready");
            resolveWaiters(token);
          },
          "expired-callback": () => {
            tokenRef.current = null;
            window.turnstile?.reset(widgetIdRef.current ?? undefined);
          },
          "timeout-callback": () => {
            tokenRef.current = null;
            window.turnstile?.reset(widgetIdRef.current ?? undefined);
          },
          "error-callback": () => {
            tokenRef.current = null;
            setStatus("error");
            resolveWaiters(null);
          },
        });
      })
      .catch(() => {
        if (!cancelled) {
          setStatus("error");
          resolveWaiters(null);
        }
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [enabled, resolveWaiters]);

  const getToken = useCallback(async () => {
    if (!enabled) return null;
    if (tokenRef.current) return tokenRef.current;

    return new Promise<string | null>((resolve) => {
      waitersRef.current.push(resolve);
      const timeout = window.setTimeout(() => {
        waitersRef.current = waitersRef.current.filter((fn) => fn !== resolve);
        resolve(tokenRef.current);
      }, 15000);
      const wrapped = (token: string | null) => {
        window.clearTimeout(timeout);
        resolve(token);
      };
      waitersRef.current[waitersRef.current.length - 1] = wrapped;

      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.execute(widgetIdRef.current);
      }
    });
  }, [enabled]);

  const reset = useCallback(() => {
    tokenRef.current = null;
    if (window.turnstile && widgetIdRef.current) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  return (
    <TurnstileContext.Provider value={{ status, getToken, reset }}>
      {children}
      {enabled && (
        <div
          ref={containerRef}
          aria-hidden="true"
          className="fixed bottom-0 left-0 w-0 h-0 overflow-hidden pointer-events-none"
        />
      )}
    </TurnstileContext.Provider>
  );
}
